import { useState } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { Box, Tabs, VStack, Text } from "@chakra-ui/react"
import { Section, SectionRoot } from "@hydrologiq/shared/chakra/custom/Section"
import { DataConnections } from "../components/data-connections/DataConnections"
import { RemoveSystemDialog } from "../components/RemoveSystemDialog"
import { useRemoveSystem } from "../hooks/useRemoveSystem"
import { useSystemInfo, useSystemRefetch } from "../providers/helpers/useSystem"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { AlertConfiguration } from "../components/alert-configuration/AlertConfiguration"

export function Settings() {
  const { systemId = "" } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const activeTab =
    tabParam === "alert-configuration" || tabParam === "admin"
      ? tabParam
      : "connections"

  const navigate = useNavigate()
  const route = useRoute()
  const systemInfo = useSystemInfo()
  const systemRefetch = useSystemRefetch()
  const { removeSystem, removing } = useRemoveSystem()
  const [dialogOpen, setDialogOpen] = useState(false)

  const systemName = systemInfo?.name ?? "this system"
  const hasAssets = systemInfo?.assets ? Object.keys(systemInfo.assets).length > 0 : false

  const handleTabChange = (details: { value: string }) => {
    setSearchParams({ tab: details.value }, { replace: true })
    if (details.value === "admin") {
      systemRefetch?.()
    }
  }

  const handleRemoveConfirm = async () => {
    try {
      await removeSystem(systemId)
      setDialogOpen(false)
      toaster.success({ title: `System '${systemName}' has been removed` })
      navigate(route)
    } catch {
      toaster.error({ title: "Failed to remove system" })
    }
  }

  return (
    <SectionRoot>
      <Tabs.Root value={activeTab} variant={"sectionLozenge" as any} onValueChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Trigger value="connections">Data Connections</Tabs.Trigger>
          <Tabs.Trigger value="alert-configuration">Alert Configuration</Tabs.Trigger>
          <Tabs.Trigger value="admin">Admin</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="connections">
          <DataConnections systemId={systemId} />
        </Tabs.Content>


        <Tabs.Content value="alert-configuration">
          <AlertConfiguration systemId={systemId} />
        </Tabs.Content>

        <Tabs.Content value="admin">
          <VStack gap={4} align="stretch">
            <Section heading="Remove system">
              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" color="fg.muted">
                  Once removed, all settings for this system will be deleted. This cannot be undone.
                </Text>
                {hasAssets && (
                  <Text fontSize="sm" color="orange.500">
                    Unassign all assets before removing the system.
                  </Text>
                )}
                <Box>
                  <ActionButton
                    onClick={() => setDialogOpen(true)}
                    disabled={hasAssets}
                    context={{
                      label: "Remove system",
                      description: "Permanently remove this system",
                      variant: "warning",
                    }}
                  />
                </Box>
              </VStack>
            </Section>
          </VStack>
        </Tabs.Content>
        <RemoveSystemDialog
          open={dialogOpen}
          systemName={systemName}
          onConfirm={handleRemoveConfirm}
          onClose={() => setDialogOpen(false)}
          loading={removing}
        />

      </Tabs.Root>
    </SectionRoot>
  )
}

export default Settings
